/* ============================================================
   glow-wishlist.js — פיצ'ר המועדפים (Wishlist) של Glow · Phase 1.

   קוד מקורי (vanilla, אפס ספריות). הפונקציונליות בהשראת סקימס (מתועדת
   ב-raw/wishlist-research), הקוד שלנו מאפס בקונבנציות glow-*.

   מה כאן:
   (1) store ב-localStorage — מפתח 'glow:wishlist', סכמה
       {"v":1,"items":[...handles],"updatedAt":"<ISO>"}. קריאה עמידה
       (JSON שבור/גרסה לא-מוכרת → אתחול ריק, אפס שגיאות קונסול).
       חסימת אחסון (ספארי פרטי / מלא) → fallback לזיכרון בלבד.
   (2) API ציבורי window.GlowWishlist: has/add/remove/toggle/list/count
       + paint/refresh + EVENT + STORAGE_KEY (חוזה ל-Phase 2 / עמוד המועדפים).
   (3) event delegation על [data-glow-wishlist-toggle] (עם debounce לדאבל-קליק).
   (4) צביעה (paint) של כל הלבבות הגלויים + באדג' מונה בהדר; סנכרון בין
       טאבים (storage event) ובאותו טאב (glow:wishlist:change).
   (5) צביעת תוכן דינמי: on load · on:cart-drawer:after-open ·
       dispatch:cart-drawer:refresh · MutationObserver ממוקד (live-search,
       שכפולי glow-loop-carousel, הזרקות cc-cart-cross-sell).
   (6) custom element <glow-wishlist-count> לבאדג' (is-visible כשמונה>0,
       דגל init למניעת אתחול/מנוי כפול).
   (7) אנליטיקה: Shopify.analytics.publish('glow_wishlist_add'/'..._remove',
       {handle, product_id, product_title, price, currency, item_category, placement}).
       (אין קידוד Measurement ID — ה-Custom Pixel הוא Phase 3.)
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'glow:wishlist';
  var SCHEMA_VERSION = 1;
  var CHANGE_EVENT = 'glow:wishlist:change';
  var TOGGLE_SELECTOR = '[data-glow-wishlist-toggle]';
  var DEBOUNCE_MS = 250;
  var ACTIVE_CLASS = 'is-in-wishlist';
  var POP_CLASS = 'glow-wishlist-btn--pop';

  /* מצב זיכרון: fallback כשה-localStorage חסום, ומקור-אמת מהיר לקריאות. */
  var memoryState = null;
  var storageBlocked = false;

  /* ---------- Store ---------- */

  function freshState() {
    return { v: SCHEMA_VERSION, items: [], updatedAt: new Date().toISOString() };
  }

  function normalizeState(raw) {
    if (!raw || typeof raw !== 'object') return freshState();
    if (raw.v !== SCHEMA_VERSION || !Array.isArray(raw.items)) return freshState();
    /* ניקוי הגנתי: רק מחרוזות לא-ריקות, ללא כפילויות. */
    var seen = {};
    var clean = [];
    for (var i = 0; i < raw.items.length; i++) {
      var h = raw.items[i];
      if (typeof h === 'string' && h && !seen[h]) {
        seen[h] = true;
        clean.push(h);
      }
    }
    return { v: SCHEMA_VERSION, items: clean, updatedAt: raw.updatedAt || new Date().toISOString() };
  }

  function readState() {
    if (storageBlocked && memoryState) return memoryState;
    var rawStr = null;
    try {
      rawStr = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      storageBlocked = true;
      if (!memoryState) memoryState = freshState();
      return memoryState;
    }
    if (!rawStr) {
      memoryState = memoryState || freshState();
      return memoryState;
    }
    var parsed;
    try {
      parsed = JSON.parse(rawStr);
    } catch (e) {
      parsed = null;
    }
    memoryState = normalizeState(parsed);
    return memoryState;
  }

  function writeState(state) {
    state.updatedAt = new Date().toISOString();
    memoryState = state;
    if (storageBlocked) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* מלא/חסום — ממשיכים בזיכרון בלבד, האתר לא נשבר. */
      storageBlocked = true;
    }
  }

  function list() {
    return readState().items.slice();
  }

  function count() {
    return readState().items.length;
  }

  function has(handle) {
    if (!handle) return false;
    return readState().items.indexOf(handle) !== -1;
  }

  function add(handle) {
    if (!handle) return false;
    var state = readState();
    if (state.items.indexOf(handle) !== -1) return false;
    state.items.push(handle);
    writeState(state);
    emitChange(handle, 'add');
    return true;
  }

  function remove(handle) {
    if (!handle) return false;
    var state = readState();
    var idx = state.items.indexOf(handle);
    if (idx === -1) return false;
    state.items.splice(idx, 1);
    writeState(state);
    emitChange(handle, 'remove');
    return true;
  }

  function toggle(handle) {
    /* דטרמיניסטי מול ה-store — אין מצב ביניים תקוע. */
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  function emitChange(handle, action) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      bubbles: true,
      detail: { handle: handle, action: action, count: count() }
    }));
  }

  /* ---------- אנליטיקה ---------- */

  function publishAnalytics(action, btn) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;
    var eventName = action === 'add' ? 'glow_wishlist_add' : 'glow_wishlist_remove';
    var placement = btn.getAttribute('data-glow-wishlist-placement') || 'card';
    /* עידון placement בזמן ריצה: כרטיס בתוך תוצאות חיפוש → 'search'. */
    if (placement === 'card' && btn.closest('.main-search__results, .template-search, .search-page')) {
      placement = 'search';
    }
    var priceStr = btn.getAttribute('data-glow-wishlist-price');
    var payload = {
      handle: btn.getAttribute('data-glow-wishlist-handle') || '',
      product_id: btn.getAttribute('data-glow-wishlist-product-id') || '',
      product_title: btn.getAttribute('data-glow-wishlist-title') || '',
      price: priceStr ? parseFloat(priceStr) : null,
      currency: btn.getAttribute('data-glow-wishlist-currency') || '',
      item_category: btn.getAttribute('data-glow-wishlist-category') || '',
      placement: placement
    };
    /* B3 — פרמטר event-scoped לדירוג ב-Looker (שם-המוצר יושב גם ב-items[]). הוספה בלבד. */
    payload.wishlist_item = payload.product_title;
    try {
      window.Shopify.analytics.publish(eventName, payload);
    } catch (e) { /* אנליטיקה לא-קריטית — לעולם לא שוברת את הפיצ'ר. */ }
  }

  /* ---------- Paint (צביעת מצב) ---------- */

  function paintButton(btn, active) {
    var isActive = typeof active === 'boolean' ? active : has(btn.getAttribute('data-glow-wishlist-handle'));
    btn.classList.toggle(ACTIVE_CLASS, isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    var labelAdd = btn.getAttribute('data-glow-wishlist-label-add') || 'הוספה למועדפים';
    var labelRemove = btn.getAttribute('data-glow-wishlist-label-remove') || 'הסרה מהמועדפים';
    btn.setAttribute('aria-label', isActive ? labelRemove : labelAdd);
  }

  function paint(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll(TOGGLE_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      paintButton(buttons[i], null);
    }
    /* אם ה-root עצמו הוא כפתור (הזרקה בודדת). */
    if (scope.nodeType === 1 && scope.matches && scope.matches(TOGGLE_SELECTOR)) {
      paintButton(scope, null);
    }
  }

  function updateBadges() {
    var c = count();
    var badges = document.querySelectorAll('glow-wishlist-count');
    for (var i = 0; i < badges.length; i++) {
      if (typeof badges[i].render === 'function') badges[i].render(c);
    }
  }

  /* ---------- Custom element: באדג' המונה ---------- */

  class GlowWishlistCount extends HTMLElement {
    connectedCallback() {
      if (this._glowInit) return; /* דגל init — מונע אתחול/מנוי כפול. */
      this._glowInit = true;
      this.setAttribute('aria-hidden', 'true');
      this.render(count());
    }

    render(c) {
      const n = typeof c === 'number' ? c : count();
      this.textContent = n > 0 ? String(n) : '';
      this.classList.toggle('is-visible', n > 0);
      this.hidden = n <= 0; /* מסיר את מאפיין hidden ההתחלתי כשיש פריטים. */
    }
  }

  if (!window.customElements.get('glow-wishlist-count')) {
    try {
      window.customElements.define('glow-wishlist-count', GlowWishlistCount);
    } catch (e) { /* דפדפן ישן — הבאדג' פשוט לא יופיע, שאר הפיצ'ר עובד. */ }
  }

  /* ---------- אינטראקציה ---------- */

  var debounceMap = {};

  function onToggleClick(btn) {
    var handle = btn.getAttribute('data-glow-wishlist-handle');
    if (!handle) return;
    var now = Date.now();
    if (debounceMap[handle] && (now - debounceMap[handle]) < DEBOUNCE_MS) return;
    debounceMap[handle] = now;

    var action = toggle(handle) ? 'add' : 'remove';

    /* micro-pop רק בהוספה, ורק אם המשתמש לא ביקש reduced-motion. */
    if (action === 'add' && !prefersReducedMotion()) {
      btn.classList.remove(POP_CLASS);
      /* reflow לאתחול האנימציה מחדש. */
      void btn.offsetWidth;
      btn.classList.add(POP_CLASS);
    }

    publishAnalytics(action, btn);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest(TOGGLE_SELECTOR) : null;
    if (!btn) return;
    evt.preventDefault();
    onToggleClick(btn);
  });

  document.addEventListener('animationend', function (evt) {
    if (evt.target.classList && evt.target.classList.contains(POP_CLASS)) {
      evt.target.classList.remove(POP_CLASS);
    }
  });

  /* ---------- סנכרון + צביעה מחדש ---------- */

  document.addEventListener(CHANGE_EVENT, function () {
    paint(document);
    updateBadges();
  });

  window.addEventListener('storage', function (evt) {
    if (evt.key !== STORAGE_KEY) return;
    /* טאב אחר עדכן — ריענון state מהאחסון וצביעה מחדש. */
    memoryState = null;
    readState();
    paint(document);
    updateBadges();
  });

  document.addEventListener('on:cart-drawer:after-open', function () {
    var drawer = document.querySelector('cart-drawer, .cart-drawer, #cart-drawer');
    paint(drawer || document);
  });

  document.addEventListener('dispatch:cart-drawer:refresh', function () {
    /* התוכן מוחלף אסינכרונית — צובעים אחרי הפריים הבא. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { paint(document); });
    });
  });

  /* MutationObserver ממוקד — גיבוי לכרטיסים דינמיים (live-search, שכפולי
     loop-carousel, הזרקות cross-sell). דחוס כדי לא להעמיס. */
  var repaintScheduled = false;
  function scheduleRepaint() {
    if (repaintScheduled) return;
    repaintScheduled = true;
    window.requestAnimationFrame(function () {
      repaintScheduled = false;
      paint(document);
    });
  }

  function nodeHasToggle(node) {
    if (node.nodeType !== 1) return false;
    if (node.matches && node.matches(TOGGLE_SELECTOR)) return true;
    return node.querySelector ? !!node.querySelector(TOGGLE_SELECTOR) : false;
  }

  function startObserver() {
    if (!('MutationObserver' in window) || !document.body) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasToggle(added[j])) { scheduleRepaint(); return; }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- אתחול ---------- */

  function init() {
    readState();
    paint(document);
    updateBadges();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------- API ציבורי (חוזה Phase 2) ---------- */

  window.GlowWishlist = {
    STORAGE_KEY: STORAGE_KEY,
    EVENT: CHANGE_EVENT,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    list: list,
    count: count,
    paint: paint,
    refresh: function () { memoryState = null; readState(); paint(document); updateBadges(); }
  };
})();

/* ============================================================
   מודול עמוד המועדפים (Phase 2) — רץ **רק** אם קיים [data-glow-wishlist-page]
   בדף (עמוד /pages/wishlist). משתמש ב-API הציבורי window.GlowWishlist בלבד —
   אפס נגיעה בלוגיקת ה-store שלמעלה. IIFE נפרד ומבודד.

   זרימה: skeleton → GlowWishlist.list() → fetch מקבילי מוגבל (~6) ל-
   /products/{handle}.js → רינדור כרטיסים בשפת העיצוב של הקטלוג. 404 → הסרה
   שקטה מה-store. הסרה (מהלב שעל הכרטיס — הדפוס הגלובלי מטפל ב-toggle) → הכרטיס
   יוצא מהגריד באנימציה (reduced-motion מכובד) + מעבר למצב-ריק אם אחרון.
   add-to-cart = data-glow-quick-add → glow-cart.js הקיים (gate/bundle · אפס לוגיקה חדשה).
   סנכרון: glow:wishlist:change (אותו טאב) · storage (טאבים) · pageshow (bfcache).
   ============================================================ */
(function () {
  'use strict';

  var container = document.querySelector('[data-glow-wishlist-page]');
  if (!container) return;

  var W = window.GlowWishlist;
  if (!W || typeof W.list !== 'function') return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    ? window.Shopify.routes.root : '/';
  var FETCH_LIMIT = 6;

  var HEART_SVG =
    '<svg class="icon icon-heart" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" role="presentation">' +
    '<path d="M12 20C12 20 3.5 14.5 3.5 8.9C3.5 6.4 5.5 4.5 7.9 4.5C9.6 4.5 11.1 5.5 12 7C12.9 5.5 14.4 4.5 16.1 4.5C18.5 4.5 20.5 6.4 20.5 8.9C20.5 14.5 12 20 12 20Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '</svg>';

  var skeleton = container.querySelector('[data-glow-wishlist-skeleton]');
  var grid = container.querySelector('[data-glow-wishlist-grid]');
  var emptyEl = container.querySelector('[data-glow-wishlist-empty]');
  var liveEl = container.querySelector('[data-glow-wishlist-live]');
  var captureEl = document.querySelector('[data-glow-wishlist-capture]');

  var cfg = {
    addLabel: container.getAttribute('data-add-label') || 'הוספה לעגלה',
    soldoutLabel: container.getAttribute('data-soldout-label') || 'אזל מהמלאי',
    /* סבב 4 (R4-5): handles שהתגית שלהם "חסר במלאי / Coming soon" (snippets/glow-product-soldout ·
       מחושב ב-Liquid בסקשן, כי ב-/products/{handle}.js אין מטה-פילדים) */
    soldoutHandles: (container.getAttribute('data-soldout-handles') || '').split(',').filter(Boolean),
    heartAdd: container.getAttribute('data-heart-label-add') || 'הוספה למועדפים',
    heartRemove: container.getAttribute('data-heart-label-remove') || 'הסרה מהמועדפים',
    currency: container.getAttribute('data-currency') || 'ILS',
    locale: container.getAttribute('data-locale') || 'he'
  };

  var cardMap = {};   /* handle → <li> */
  var cache = {};     /* handle → product JSON */
  var initialDone = false;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- עזרי מוצר ---------- */

  function formatMoney(cents) {
    var amount = (typeof cents === 'number' ? cents : 0) / 100;
    try {
      return new Intl.NumberFormat(cfg.locale, {
        style: 'currency', currency: cfg.currency, minimumFractionDigits: 0, maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      var n = amount % 1 === 0 ? String(amount) : amount.toFixed(2);
      return n + ' ₪';
    }
  }

  function sizedImage(url, w) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + w;
  }

  function firstAvailableVariant(product) {
    if (!product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  function productUrl(product, handle) {
    if (product && product.url) return ROOT.replace(/\/$/, '') + product.url;
    return ROOT + 'products/' + handle;
  }

  /* ---------- בניית כרטיס ---------- */

  function buildCard(handle, product) {
    var li = document.createElement('li');
    li.className = 'glow-wl-page__card';
    li.setAttribute('data-glow-wl-card', '');
    li.setAttribute('data-handle', handle);
    if (product.id) li.setAttribute('data-product-id', product.id);

    var title = product.title || handle;
    var url = productUrl(product, handle);

    /* מדיה + לב */
    var mediaWrap = document.createElement('div');
    mediaWrap.className = 'glow-wl-page__card-media-wrap';

    var mediaLink = document.createElement('a');
    mediaLink.className = 'glow-wl-page__card-link';
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', title);

    var imgSrc = product.featured_image
      || (product.media && product.media[0] && product.media[0].preview_image && product.media[0].preview_image.src)
      || (product.images && product.images[0]);
    if (imgSrc) {
      var img = document.createElement('img');
      img.className = 'glow-wl-page__card-img';
      img.src = sizedImage(imgSrc, 540);
      img.alt = title;
      img.loading = 'lazy';
      img.decoding = 'async';
      var prev = product.media && product.media[0] && product.media[0].preview_image;
      if (prev && prev.width && prev.height) { img.width = prev.width; img.height = prev.height; }
      mediaLink.appendChild(img);
    }
    mediaWrap.appendChild(mediaLink);

    var heart = document.createElement('button');
    heart.type = 'button';
    heart.className = 'glow-wishlist-btn glow-wishlist-btn--wishlist-page is-in-wishlist';
    heart.setAttribute('data-glow-wishlist-toggle', '');
    heart.setAttribute('data-glow-wishlist-handle', handle);
    heart.setAttribute('data-glow-wishlist-placement', 'wishlist_page');
    heart.setAttribute('data-glow-wishlist-label-add', cfg.heartAdd);
    heart.setAttribute('data-glow-wishlist-label-remove', cfg.heartRemove);
    if (product.id) heart.setAttribute('data-glow-wishlist-product-id', product.id);
    heart.setAttribute('data-glow-wishlist-title', title);
    if (typeof product.price === 'number') heart.setAttribute('data-glow-wishlist-price', (product.price / 100));
    heart.setAttribute('data-glow-wishlist-currency', cfg.currency);
    if (product.type) heart.setAttribute('data-glow-wishlist-category', product.type);
    heart.setAttribute('aria-pressed', 'true');
    heart.setAttribute('aria-label', cfg.heartRemove);
    heart.innerHTML = '<span class="glow-wishlist-btn__icon" aria-hidden="true">' + HEART_SVG + '</span>';
    mediaWrap.appendChild(heart);

    li.appendChild(mediaWrap);

    /* גוף */
    var body = document.createElement('div');
    body.className = 'glow-wl-page__card-body';

    var titleLink = document.createElement('a');
    titleLink.className = 'glow-wl-page__card-title-link';
    titleLink.href = url;
    var h = document.createElement('h2');
    h.className = 'glow-wl-page__card-title';
    h.textContent = title;
    titleLink.appendChild(h);
    body.appendChild(titleLink);

    var priceEl = document.createElement('div');
    priceEl.className = 'glow-wl-page__card-price';
    var bdi = document.createElement('bdi');
    bdi.textContent = formatMoney(product.price);
    priceEl.appendChild(bdi);
    body.appendChild(priceEl);

    var variant = firstAvailableVariant(product);
    var taggedSoldout = cfg.soldoutHandles.indexOf(product.handle || handle) !== -1;
    if (product.available && variant && !taggedSoldout) {
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'glow-wl-page__card-add';
      addBtn.setAttribute('data-glow-quick-add', '');
      addBtn.setAttribute('data-glow-list', 'wishlist');
      addBtn.setAttribute('data-variant-id', variant.id);
      addBtn.setAttribute('aria-label', cfg.addLabel + ': ' + title);
      addBtn.textContent = cfg.addLabel;
      body.appendChild(addBtn);
    } else {
      var soldout = document.createElement('button');
      soldout.type = 'button';
      soldout.className = 'glow-wl-page__card-add glow-wl-page__card-add--soldout';
      soldout.disabled = true;
      soldout.setAttribute('aria-disabled', 'true');
      soldout.textContent = cfg.soldoutLabel;
      body.appendChild(soldout);
    }

    li.appendChild(body);
    return li;
  }

  function appendCard(handle, product) {
    if (cardMap[handle]) return;
    var li = buildCard(handle, product);
    cardMap[handle] = li;
    grid.appendChild(li);
  }

  /* ---------- fetch עם מגבלת מקביליות ---------- */

  function fetchProduct(handle) {
    var url = ROOT + 'products/' + encodeURIComponent(handle) + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (res.status === 404) return null;      /* נמחק/הוסתר → הסרה שקטה */
        if (!res.ok) throw new Error('status ' + res.status);
        return res.json();
      })
      .then(function (data) { if (data) cache[handle] = data; return data; })
      .catch(function () { return undefined; });   /* שגיאת רשת → לא מסירים (retry עתידי) */
  }

  function pool(items, worker, limit) {
    return new Promise(function (resolve) {
      var i = 0, active = 0, done = 0, total = items.length;
      if (!total) { resolve(); return; }
      function next() {
        while (active < limit && i < total) {
          var it = items[i++];
          active++;
          Promise.resolve(worker(it)).catch(function () {}).then(function () {
            active--; done++;
            if (done === total) resolve(); else next();
          });
        }
      }
      next();
    });
  }

  /* ---------- מצבים ---------- */

  function hideSkeleton() { if (skeleton) skeleton.hidden = true; }

  function countCards() {
    var n = 0;
    for (var k in cardMap) { if (Object.prototype.hasOwnProperty.call(cardMap, k)) n++; }
    return n;
  }

  function evaluateState() {
    var hasCards = countCards() > 0;
    if (grid) grid.hidden = !hasCards;
    if (emptyEl) emptyEl.hidden = hasCards;
    if (captureEl) captureEl.hidden = !hasCards;
  }

  function announce(msg) {
    if (!liveEl) return;
    liveEl.textContent = '';
    window.requestAnimationFrame(function () { liveEl.textContent = msg; });
  }

  /* ---------- הסרה מהגריד ---------- */

  function removeCard(handle, animate) {
    var el = cardMap[handle];
    if (!el) return;
    delete cardMap[handle];

    function done() {
      if (el.parentNode) el.parentNode.removeChild(el);
      evaluateState();
    }

    if (animate && !prefersReducedMotion()) {
      el.classList.add('glow-wl-page__card--removing');
      var finished = false;
      var onEnd = function () { if (finished) return; finished = true; done(); };
      el.addEventListener('transitionend', onEnd, { once: true });
      /* fallback אם transitionend לא נורה */
      window.setTimeout(onEnd, 400);
    } else {
      done();
    }
    announce('הוסר מהמועדפים');
  }

  function reconcileAdd(handle) {
    if (cardMap[handle]) return;
    if (cache[handle]) { appendCard(handle, cache[handle]); evaluateState(); return; }
    fetchProduct(handle).then(function (data) {
      if (data === null) { try { W.remove(handle); } catch (e) { /* noop */ } evaluateState(); return; }
      if (!data) return;
      appendCard(handle, data);
      evaluateState();
    });
  }

  /* מיישר את הגריד מול ה-store (טאבים / bfcache) — בלי אנימציה */
  function reconcile() {
    var listNow = W.list();
    var present = {};
    listNow.forEach(function (h) { present[h] = true; });
    /* הסרת כרטיסים שכבר לא ברשימה */
    for (var h in cardMap) {
      if (Object.prototype.hasOwnProperty.call(cardMap, h) && !present[h]) {
        removeCard(h, false);
      }
    }
    /* הוספת כרטיסים חדשים */
    listNow.forEach(function (hh) { if (!cardMap[hh]) reconcileAdd(hh); });
    evaluateState();
  }

  /* ---------- רינדור ראשוני ---------- */

  function renderAll() {
    var handles = W.list();
    if (!handles.length) { hideSkeleton(); initialDone = true; evaluateState(); return; }

    pool(handles, function (handle) {
      return fetchProduct(handle).then(function (data) {
        if (data === null) { try { W.remove(handle); } catch (e) { /* noop */ } }
      });
    }, FETCH_LIMIT).then(function () {
      hideSkeleton();
      /* מרנדר לפי סדר ה-store (אחרי ניקוי ה-404) */
      W.list().forEach(function (handle) {
        var data = cache[handle];
        if (data) appendCard(handle, data);
      });
      initialDone = true;
      evaluateState();
    });
  }

  /* ---------- סנכרון ---------- */

  document.addEventListener(W.EVENT, function (evt) {
    if (!initialDone) return;
    var detail = evt.detail || {};
    if (detail.action === 'remove') {
      removeCard(detail.handle, true);
    } else if (detail.action === 'add') {
      reconcileAdd(detail.handle);
    }
  });

  window.addEventListener('storage', function (evt) {
    if (evt.key !== W.STORAGE_KEY) return;
    if (!initialDone) return;
    /* ה-store כבר ריענן state ב-handler שלו; כאן רק מיישרים את הגריד */
    window.requestAnimationFrame(reconcile);
  });

  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted && initialDone) reconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll);
  } else {
    renderAll();
  }
})();
